import { registerApplication, start } from "single-spa";
import {
    constructApplications,
    constructRoutes,
    constructLayoutEngine,
} from "single-spa-layout";

import layout from "./microfrontend-layout.html";
import webLoader from "/assets/loader/jopit_web_loader.mp4";
import {FirebaseApplication} from "@jopitnow/web-toolkit";
import "./index.css";
import "./scroll/scroll-shadow.js";
import { createLandingLoader } from "./prerender/landingLoader.js";
import { adoptPrerenderShell, removeOrphanPrerenderShell } from "./prerender/prerenderShell.js";

// `playsinline` no es opcional: en iPhone, Safari se lleva a su reproductor
// nativo a pantalla completa cualquier <video> que arranque sin ese atributo.
// El loader de 128px pasaba a ocupar toda la pantalla con franjas negras arriba
// y abajo cada vez que un MFE tardaba lo suficiente como para que el loader
// llegara a reproducirse.
const gifLoader = `<div class="gif_container">
            <video class="gif_video" autoplay muted loop playsinline>
                <source src=${webLoader} type="video/mp4">
            </video>
        </div>`;

const data = {
    loaders: {
        gifLoader,
        // Las rutas del landing (JOP-75): el mismo spinner, pero no pinta sobre
        // el HTML prerenderizado (ver src/prerender/landingLoader.js).
        landingLoader: createLandingLoader(gifLoader),
    }
}

const config = {
    apiKey: process.env["FIREBASE_API_KEY"],
    authDomain: process.env["FIREBASE_AUTH_DOMAIN"],
    projectId: process.env["FIREBASE_PROJECT_ID"],
    storageBucket: process.env["FIREBASE_STORAGE_BUCKET"],
    messagingSenderId: process.env["FIREBASE_MESSAGING_SENDER_ID"],
    appId: process.env["FIREBASE_APP_ID"],
    measurementId: process.env["FIREBASE_MEASUREMENT_ID"]
};

// Configuración de analytics de producto, en un global y no en el bundle de cada
// MFE: los MFEs se despliegan por separado, así que cambiar esto no puede
// depender de redesplegar veinte repos.
//
// El project token de Mixpanel ya NO viaja acá. Es público por diseño y con él
// cualquiera puede inyectar eventos falsos o inflar la factura, así que los
// eventos salen contra un proxy propio (`jopit.web.analytics-proxy`) que inyecta
// el token del lado del server. `mixpanelToken` queda como relleno: el SDK
// necesita un string para arrancar y lo manda dentro del payload, donde el proxy
// lo reemplaza por el real.
//
// OJO: ese relleno siembra la clave de localStorage (`mp_jopit-web_mixpanel`)
// donde vive el distinct_id. Si se cambia, todos los visitantes pierden su
// identidad y se cuentan como usuarios nuevos.
const ingestHost = process.env["ANALYTICS_INGEST_HOST"];

window.__JOPIT_ANALYTIC__ = {
    // Sin host de ingesta el proveedor queda en no-op silencioso, que es lo que
    // queremos en local y en los tests.
    mixpanelToken: ingestHost ? "jopit-web" : undefined,
    apiHost: ingestHost,
    app: "shop",
    environment: process.env.NODE_ENV === "production" ? "production" : "staging",
    debug: false
};

await FirebaseApplication.init(config)

const routes = constructRoutes(layout, data);

// En las páginas prerenderizadas, el <main> del HTML pasa a ser el del layout, así el
// layout no mueve el contenedor del landing: moverlo hacía que Chrome volviera a
// registrar el titular para LCP (src/prerender/prerenderShell.js). Va antes de
// constructLayoutEngine, que sin `active: false` ya activa el layout al construirse.
adoptPrerenderShell(routes);

const applications = constructApplications({
    routes,
    loadApp({ name }) {
        return System.import(name);
    },
});

const layoutEngine = constructLayoutEngine({ routes, applications });

// Shared MFEs (login, shop-profile) are the same bundle mounted by both the
// shop and the marketplace root-configs, on the same routes — they can't tell
// which initiative they run under from the URL. We hand every app an
// `initiative` customProp so they can branch runtime behaviour (e.g. where the
// profile logout redirects, or the login post-auth default).
applications.forEach((application) =>
    registerApplication({
        ...application,
        customProps: (name, location) => ({
            ...(typeof application.customProps === "function"
                ? application.customProps(name, location)
                : application.customProps),
            initiative: "shop",
        }),
    })
);

layoutEngine.activate();
// El layout ya está activo (constructLayoutEngine lo activa al construirse). Si no se
// adoptó el shell (no hay prerender, o no era lo primero del body), el contenedor ya
// está en el <main> del layout y el del HTML prerenderizado quedó vacío.
removeOrphanPrerenderShell();
start();
